/**
 * Utilities for managing threads in Supabase
 * @module threadUtils
 */

import { supabase } from "@/lib/supabase";

// Constants
const TABLE_NAME = "threads";
export const ThreadStatus = {
  ACTIVE: "active",
  ARCHIVED: "archived",
  DELETED: "deleted",
};
const BATCH_SIZE = 100;

export const createThread = async ({
  id = null, // OPTIMISTIC RENDERING: Accept pre-generated thread ID
  openaiThreadId,
  workspaceId,
  assistantId,
  userId,
  title,
  toolResources = {},
  metadata = null,
}) => {
  try {
    // Ensure all IDs are converted to strings
    const safeUserId = String(userId || "");
    const safeWorkspaceId = String(workspaceId || "");
    const safeAssistantId = String(assistantId || "");
    const safeOpenaiThreadId = String(openaiThreadId || "");

    // Ensure the metadata includes thread_title with properly stringified IDs
    // Use empty object if metadata is null or undefined
    let threadMetadata = metadata || {
      thread_title: title,
      user_id: safeUserId,
      workspace_id: safeWorkspaceId,
      assistant_id: safeAssistantId,
      openai_thread_id: safeOpenaiThreadId,
      created_at: new Date().toISOString(),
    };

    // Perform a safety check on threadMetadata - re-create if somehow null
    if (!threadMetadata) {
      threadMetadata = {
        thread_title: title,
        user_id: safeUserId,
        workspace_id: safeWorkspaceId,
        assistant_id: safeAssistantId,
        openai_thread_id: safeOpenaiThreadId,
        created_at: new Date().toISOString(),
      };
    }

    // If using provided metadata, ensure essential fields are present and are strings
    // Access threadMetadata safely with null checks at each step
    if (metadata) {
      if (!threadMetadata.thread_title) threadMetadata.thread_title = title;

      if (!threadMetadata.user_id) threadMetadata.user_id = safeUserId;
      else threadMetadata.user_id = String(threadMetadata.user_id || "");

      if (!threadMetadata.workspace_id)
        threadMetadata.workspace_id = safeWorkspaceId;
      else
        threadMetadata.workspace_id = String(threadMetadata.workspace_id || "");

      if (!threadMetadata.assistant_id)
        threadMetadata.assistant_id = safeAssistantId;
      else
        threadMetadata.assistant_id = String(threadMetadata.assistant_id || "");

      if (!threadMetadata.openai_thread_id)
        threadMetadata.openai_thread_id = safeOpenaiThreadId;
      else
        threadMetadata.openai_thread_id = String(
          threadMetadata.openai_thread_id || ""
        );

      if (!threadMetadata.created_at)
        threadMetadata.created_at = new Date().toISOString();
    }

    // Validate metadata to ensure all IDs are strings
    if (threadMetadata && typeof threadMetadata === "object") {
      Object.keys(threadMetadata).forEach((key) => {
        if (
          key.includes("id") &&
          threadMetadata[key] !== null &&
          typeof threadMetadata[key] !== "string"
        ) {
          threadMetadata[key] = String(threadMetadata[key] || "");
        }
      });
    } else {
      // If threadMetadata is somehow still null or not an object, create a minimal valid object
      threadMetadata = {
        thread_title: title,
        user_id: safeUserId,
        created_at: new Date().toISOString(),
      };
    }

    // OPTIMISTIC RENDERING: Build insert object with optional id field
    const insertData = {
      openai_thread_id: safeOpenaiThreadId,
      workspace_id: safeWorkspaceId,
      assistant_id: safeAssistantId,
      user_id: safeUserId,
      title,
      metadata: threadMetadata,
      tool_resources: toolResources || {},
      // CRITICAL FIX: Include id if provided for optimistic rendering
      ...(id && { id }),
    };

    const { data, error } = await supabase
      .from(TABLE_NAME)
      .insert(insertData)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { thread: data, error: null };
  } catch (error) {
    return { thread: null, error };
  }
};

export const getThread = async ({
  supabaseThreadId,
  workspaceId,
  columns = "*",
}) => {
  try {
    let query = supabase
      .from(TABLE_NAME)
      .select(columns)
      .eq("id", supabaseThreadId);

    if (workspaceId) {
      query = query.eq("workspace_id", workspaceId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) throw error;
    return { thread: data, error: null };
  } catch (error) {
    return { thread: null, error };
  }
};

export const updateThread = async ({ supabaseThreadId, updates }) => {
  try {
    // Regular update path if no title lock or force update requested
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", supabaseThreadId)
      .select();

    if (error) throw error;
    return { thread: data ? data[0] : null, error: null };
  } catch (error) {
    return { thread: null, error };
  }
};

export const updateThreadMetadata = async ({ supabaseThreadId, metadata }) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({
        metadata,
        updated_at: new Date().toISOString(),
      })
      .eq("id", supabaseThreadId)
      .select()
      .single();

    if (error) throw error;
    return { thread: data, error: null };
  } catch (error) {
    return { thread: null, error };
  }
};

export const deleteThread = async ({ supabaseThreadId }) => {
  if (!supabaseThreadId) {
    throw new Error("Thread ID is required for deletion");
  }

  try {
    // First delete all child records to avoid foreign key constraints

    // 1. Delete all thread messages
    const { error: messagesError } = await supabase
      .from("thread_messages")
      .delete()
      .eq("thread_id", supabaseThreadId);

    if (messagesError) {
      console.error("Error deleting thread messages:", messagesError);
      // Continue with deletion even if message deletion fails
    }

    // 2. Note: tool_executions table deletion removed as table is deprecated

    // 3. Finally delete the thread itself
    const { data, error } = await supabase
      .from("threads")
      .delete()
      .eq("id", supabaseThreadId)
      .select("id")
      .maybeSingle();

    if (error) {
      // Check if it's a PGRST116 error (thread doesn't exist)
      if (error.code === "PGRST116") {
        // Thread doesn't exist, treat as successful deletion (idempotent)
        console.log(
          `Thread ${supabaseThreadId} doesn't exist, treating as already deleted`
        );
        return { success: true, threadId: supabaseThreadId };
      }
      console.error("Error deleting thread:", error);
      throw error;
    }

    // If data is null, thread didn't exist, but that's fine (idempotent operation)
    return { success: true, threadId: supabaseThreadId };
  } catch (error) {
    console.error("Error in SupabaseThreadUtils.deleteThread:", error);
    throw error;
  }
};

export const listThreads = async ({
  workspaceId,
  userId,
  status = ThreadStatus.ACTIVE,
  limit = 20,
  offset = 0,
  orderBy = "last_activity",
  ascending = false,
}) => {
  try {
    let query = supabase.from(TABLE_NAME).select("*", { count: "exact" });

    if (workspaceId) {
      query = query.eq("workspace_id", workspaceId);
    }

    if (userId) {
      query = query.eq("user_id", userId);
    }

    if (status) {
      query = query.eq("status", status);
    }

    query = query
      .order(orderBy, { ascending })
      .range(offset, offset + limit - 1);

    const { data, count, error } = await query;

    if (error) throw error;
    return { threads: data, count, error: null };
  } catch (error) {
    return { threads: [], count: 0, error };
  }
};

export const searchThreads = async ({ workspaceId, query, limit = 20 }) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select("*")
      .eq("workspace_id", workspaceId)
      .textSearch("title", query)
      .limit(limit);

    if (error) throw error;
    return { threads: data, error: null };
  } catch (error) {
    return { threads: [], error };
  }
};

export const updateThreadTitle = async ({ supabaseThreadId, title }) => {
  try {
    const { data, error } = await supabase
      .from("threads")
      .update({
        title,
        updated_at: new Date().toISOString(),
      })
      .eq("id", supabaseThreadId)
      .select()
      .single();

    if (error) throw error;
    return { thread: data, error: null };
  } catch (error) {
    return { thread: null, error };
  }
};

export const updateMessageCount = async ({
  supabaseThreadId,
  messageCount,
}) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({
        message_count: messageCount,
        last_activity: new Date().toISOString(),
      })
      .eq("id", supabaseThreadId)
      .select()
      .single();

    if (error) throw error;
    return { thread: data, error: null };
  } catch (error) {
    return { thread: null, error };
  }
};

export const updateMessageIds = async ({ supabaseThreadId, messageIds }) => {
  try {
    const { data, error } = await supabase
      .from("threads")
      .update({
        message_ids: messageIds,
        message_count: messageIds.length,
        last_message_at: new Date().toISOString(),
        last_activity: new Date().toISOString(),
      })
      .eq("id", supabaseThreadId)
      .select()
      .single();

    if (error) throw error;
    return { thread: data, error: null };
  } catch (error) {
    return { thread: null, error };
  }
};

export const updateToolOutputIds = async ({
  supabaseThreadId,
  toolOutputIds,
}) => {
  try {
    const { data, error } = await supabase
      .from("threads")
      .update({
        tool_output_ids: toolOutputIds,
        last_activity: new Date().toISOString(),
      })
      .eq("id", supabaseThreadId)
      .select()
      .single();

    if (error) throw error;
    return { thread: data, error: null };
  } catch (error) {
    return { thread: null, error };
  }
};

export const batchUpdateThreads = async ({ supabaseThreadIds, updates }) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .in("id", supabaseThreadIds)
      .select();

    if (error) throw error;
    return { threads: data, error: null };
  } catch (error) {
    return { threads: [], error };
  }
};

export const archiveThread = async ({ supabaseThreadId }) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({
        status: ThreadStatus.ARCHIVED,
        updated_at: new Date().toISOString(),
        metadata: {
          archived_at: new Date().toISOString(),
        },
      })
      .eq("id", supabaseThreadId)
      .select()
      .single();

    if (error) throw error;
    return { thread: data, error: null };
  } catch (error) {
    return { thread: null, error };
  }
};

export const unarchiveThread = async ({ supabaseThreadId }) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({
        status: ThreadStatus.ACTIVE,
        updated_at: new Date().toISOString(),
        metadata: {
          unarchived_at: new Date().toISOString(),
        },
      })
      .eq("id", supabaseThreadId)
      .select()
      .single();

    if (error) throw error;
    return { thread: data, error: null };
  } catch (error) {
    return { thread: null, error };
  }
};

export const renameThread = async ({ supabaseThreadId, title }) => {
  if (!supabaseThreadId) {
    return { success: false, message: "Thread ID is required" };
  }

  try {
    // First, verify the thread exists
    const { data: existingThread, error: checkError } = await supabase
      .from(TABLE_NAME)
      .select("id, title, metadata")
      .eq("id", supabaseThreadId)
      .single();

    if (checkError) {
      return { thread: null, error: checkError };
    }

    if (!existingThread) {
      const missingError = new Error(
        `Thread not found with ID: ${supabaseThreadId}`
      );
      return { thread: null, error: missingError };
    }

    // Check if title is already set to avoid unnecessary updates
    if (existingThread.title === title) {
      return { thread: existingThread, error: null };
    }

    // Step 2: Create updated metadata by merging with existing metadata
    const updatedMetadata = {
      ...(existingThread?.metadata || {}),
      thread_title: title,
      renamed_at: new Date().toISOString(),
    };

    // Step 3: Update the thread with the new title and merged metadata
    const updatePayload = {
      title,
      metadata: updatedMetadata,
      updated_at: new Date().toISOString(),
    };

    const { error: updateError } = await supabase
      .from(TABLE_NAME)
      .update(updatePayload)
      .eq("id", supabaseThreadId);

    if (updateError) {
      return { thread: null, error: updateError };
    }

    const { data: updatedThread, error: fetchError } = await supabase
      .from(TABLE_NAME)
      .select("*")
      .eq("id", supabaseThreadId)
      .single();

    if (fetchError) {
      // Return a manually constructed thread with the updated title
      const constructedThread = {
        ...existingThread,
        title,
        metadata: updatedMetadata,
        updated_at: new Date().toISOString(),
      };

      return { thread: constructedThread, error: null };
    }

    return { thread: updatedThread, error: null };
  } catch (error) {
    return { thread: null, error };
  }
};

/**
 * Verifies and fixes a thread's title and metadata.thread_title for consistency
 * @param {string} supabaseThreadId - The Supabase thread ID
 * @returns {Object} The verification and fix result
 */
export const verifyAndFixThreadTitle = async ({ supabaseThreadId }) => {
  try {
    // Step 1: Get the current thread
    const { data: thread, error: fetchError } = await supabase
      .from(TABLE_NAME)
      .select("*")
      .eq("id", supabaseThreadId)
      .single();

    if (fetchError) {
      throw fetchError;
    }

    if (!thread) {
      throw new Error(`Thread not found with ID: ${supabaseThreadId}`);
    }

    // Step 2: Check if metadata exists and if thread_title matches title
    const metadata = thread.metadata || {};
    const title = thread.title || "Untitled Thread";
    const metadataTitle = metadata.thread_title;

    // If metadata.thread_title already matches title, no fix needed
    if (metadataTitle === title) {
      return {
        thread,
        fixed: false,
        error: null,
        message: "Thread title is already consistent",
      };
    }

    // Step 3: Update the metadata to include thread_title if missing or different
    const updatedMetadata = {
      ...metadata,
      thread_title: title,
      verified_at: new Date().toISOString(),
    };

    // Step 4: Update the thread with the fixed metadata
    const { error: updateError } = await supabase
      .from(TABLE_NAME)
      .update({
        metadata: updatedMetadata,
        updated_at: new Date().toISOString(),
      })
      .eq("id", supabaseThreadId);

    if (updateError) {
      throw updateError;
    }

    // Step 5: Refetch the updated thread
    const { data: updatedThread, error: refetchError } = await supabase
      .from(TABLE_NAME)
      .select("*")
      .eq("id", supabaseThreadId)
      .single();

    if (refetchError) {
      // Return a manually updated thread as fallback
      const fixedThread = {
        ...thread,
        metadata: updatedMetadata,
        updated_at: new Date().toISOString(),
      };

      return {
        thread: fixedThread,
        fixed: true,
        error: null,
        message: "Thread title fixed (manually constructed)",
      };
    }

    return {
      thread: updatedThread,
      fixed: true,
      error: null,
      message: "Thread title fixed successfully",
    };
  } catch (error) {
    return {
      thread: null,
      fixed: false,
      error,
      message: "Error fixing thread title",
    };
  }
};

/**
 * Fix all thread titles to ensure consistency between title and metadata.thread_title
 * @returns {Object} The results of the fix operation
 */
export const fixAllThreadTitles = async () => {
  try {
    // Get all threads
    const { data: threads, error: fetchError } = await supabase
      .from(TABLE_NAME)
      .select("id, title, metadata");

    if (fetchError) {
      throw fetchError;
    }

    const results = {
      total: threads.length,
      fixed: 0,
      alreadyConsistent: 0,
      failed: 0,
      errors: [],
    };

    // Process each thread
    for (const thread of threads) {
      try {
        const result = await verifyAndFixThreadTitle({
          supabaseThreadId: thread.id,
        });

        if (result.fixed) {
          results.fixed++;
        } else {
          results.alreadyConsistent++;
        }
      } catch (error) {
        results.failed++;
        results.errors.push({
          threadId: thread.id,
          error: error.message,
        });
      }
    }

    return results;
  } catch (error) {
    return { results: null, error };
  }
};

/**
 * Subscribe to real-time thread updates for a workspace
 *
 * @param {string} workspaceId - ID of the workspace to monitor
 * @param {function} onThreadChange - Callback when a thread is changed
 * @param {function} onThreadDelete - Callback when a thread is deleted
 * @returns {object} - Supabase subscription channel
 */
export function subscribeToWorkspaceThreads(
  workspaceId,
  onThreadChange,
  onThreadDelete
) {
  try {
    if (!workspaceId) {
      return null;
    }

    // Create a channel for thread updates
    const channel = supabase
      .channel(`workspace-threads-${workspaceId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "threads",
          filter: `workspace_id=eq.${workspaceId}`,
        },
        (payload) => {
          if (onThreadChange && payload.new) {
            onThreadChange(payload.new);
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "threads",
          filter: `workspace_id=eq.${workspaceId}`,
        },
        (payload) => {
          if (onThreadDelete && payload.old) {
            onThreadDelete(payload.old);
          }
        }
      )
      .subscribe((status) => {});
    return channel;
  } catch (error) {
    return null;
  }
}

/**
 * Unsubscribe from a workspace threads channel
 *
 * @param {object} channel - The channel to unsubscribe from
 */
export function unsubscribeFromWorkspaceThreads(channel) {
  if (!channel) return;

  try {
    // Check if channel exists and has a valid unsubscribe method
    if (typeof channel.unsubscribe === "function") {
      // Try to unsubscribe using the channel's own method first
      channel.unsubscribe();
    } else if (supabase && typeof supabase.removeChannel === "function") {
      // Fallback to supabase.removeChannel if available
      // Only try to remove if channel is not already closed
      if (channel.state !== "CLOSED") {
        supabase.removeChannel(channel);
      }
    }
  } catch (error) {
    console.error("Error unsubscribing from workspace threads:", error);
  }
}

/**
 * Export a thread with all its messages for data export
 * @param {string} supabaseThreadId - The Supabase thread ID
 * @returns {Object} The thread data with messages included
 */
export const exportThread = async ({ supabaseThreadId }) => {
  try {
    if (!supabaseThreadId) {
      throw new Error("Thread ID is required for export");
    }

    // Fetch the thread data
    const { data: thread, error: threadError } = await supabase
      .from(TABLE_NAME)
      .select("*")
      .eq("id", supabaseThreadId)
      .single();

    if (threadError) {
      console.error("Error fetching thread for export:", threadError);
      throw threadError;
    }

    if (!thread) {
      throw new Error(`Thread not found with ID: ${supabaseThreadId}`);
    }

    // Fetch all messages for this thread
    const { data: messages, error: messagesError } = await supabase
      .from("thread_messages")
      .select("*")
      .eq("thread_id", supabaseThreadId)
      .order("created_at", { ascending: true });

    if (messagesError) {
      console.error("Error fetching messages for export:", messagesError);
      throw messagesError;
    }

    // Format the messages for export
    const formattedMessages = (messages || []).map((message) => ({
      id: message.message_id,
      role: message.role,
      content: message.content,
      created_at: message.created_at,
      updated_at: message.updated_at,
      metadata: message.metadata || {},
      attachments: message.attachments || [],
      annotations: message.annotations || [],
      file_ids: message.file_ids || [],
      tool_calls: message.tool_calls || [],
      provider: message.provider || "openai",
      provider_message_id: message.provider_message_id,
    }));

    // Return the thread with messages included
    const exportData = {
      ...thread,
      messages: formattedMessages,
      message_count: formattedMessages.length,
      export_timestamp: new Date().toISOString(),
    };

    return { data: exportData, error: null };
  } catch (error) {
    console.error("Error exporting thread:", error);
    return { data: null, error };
  }
};

/**
 * Update thread display order
 * @param {string} threadId - The thread ID
 * @param {number} displayOrder - The new display order
 * @returns {Promise} - The updated thread
 */
export const updateThreadOrder = async (threadId, displayOrder) => {
  try {
    const { data, error } = await supabase
      .from("threads")
      .update({ display_order: displayOrder })
      .eq("id", threadId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating thread order:", error);
    throw error;
  }
};

/**
 * Update multiple thread display orders in batch
 * @param {Array<{id: string, display_order: number}>} updates - Array of thread updates
 * @returns {Promise} - The updated threads
 */
export const updateThreadOrders = async (updates) => {
  try {
    const updatePromises = updates.map(({ id, display_order }) =>
      supabase
        .from("threads")
        .update({ display_order })
        .eq("id", id)
        .select()
        .single()
    );

    const results = await Promise.all(updatePromises);
    const errors = results.filter((r) => r.error);

    if (errors.length > 0) {
      throw new Error(`Failed to update ${errors.length} thread(s)`);
    }

    return results.map((r) => r.data);
  } catch (error) {
    console.error("Error updating thread orders:", error);
    throw error;
  }
};

/**
 * Move thread to a different workspace and update its order
 * @param {string} threadId - The thread ID
 * @param {string} targetWorkspaceId - The target workspace ID (null for Chats section)
 * @param {number} displayOrder - The new display order in the target workspace
 * @returns {Promise} - The updated thread
 */
export const moveThreadToWorkspace = async (
  threadId,
  targetWorkspaceId,
  displayOrder
) => {
  try {
    const updates = {
      workspace_id: targetWorkspaceId,
      display_order: displayOrder,
    };

    const { data, error } = await supabase
      .from("threads")
      .update(updates)
      .eq("id", threadId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error moving thread to workspace:", error);
    throw error;
  }
};

// Export all thread utilities as a single object
export const SupabaseThreadUtils = {
  createThread,
  getThread,
  updateThread,
  updateThreadMetadata,
  deleteThread,
  listThreads,
  searchThreads,
  updateThreadTitle,
  updateMessageCount,
  updateMessageIds,
  updateToolOutputIds,
  batchUpdateThreads,
  archiveThread,
  unarchiveThread,
  renameThread,
  verifyAndFixThreadTitle,
  fixAllThreadTitles,
  subscribeToWorkspaceThreads,
  unsubscribeFromWorkspaceThreads,
  exportThread,
  updateThreadOrder,
  updateThreadOrders,
  moveThreadToWorkspace,
};
